import React, { useState, useEffect, useCallback } from 'react';
import { fetchBlanksOrders, fulfillBlanksOrder } from '../api';
import BlanksFulfillModal from './BlanksFulfillModal';
import './BlanksOrders.css';

const STATUS_TABS = [
  { value: '', label: 'All' },
  { value: 'new', label: 'New' },
  { value: 'processing', label: 'Processing' },
  { value: 'shipped', label: 'Shipped' },
  { value: 'delivered', label: 'Delivered' },
  { value: 'cancelled', label: 'Cancelled' }
];

const STATUS_LABELS = {
  new: 'New',
  processing: 'Processing',
  shipped: 'Shipped',
  delivered: 'Delivered',
  cancelled: 'Cancelled'
};

const BlanksOrders = ({ session, onLogout }) => {
  const [orders, setOrders] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState('');
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [successMsg, setSuccessMsg] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [searchQuery, setSearchQuery] = useState('');

  const accessToken = session?.access_token;

  const loadOrders = useCallback(async () => {
    setIsLoading(true);
    setError('');
    try {
      const data = await fetchBlanksOrders(accessToken, statusFilter);
      setOrders(data);
    } catch (err) {
      if (err.message === 'FORBIDDEN') {
        onLogout();
        return;
      }
      setError(err.message);
    } finally {
      setIsLoading(false);
    }
  }, [accessToken, statusFilter, onLogout]);

  useEffect(() => {
    loadOrders();
  }, [loadOrders]);

  const handleFulfill = async (orderId, formData) => {
    const result = await fulfillBlanksOrder(accessToken, { orderId, ...formData });
    setSelectedOrder(null);
    const shipped = formData.fulfillmentStatus === 'shipped';
    setSuccessMsg(
      shipped
        ? `Order marked shipped.${result.emailSent ? ' Customer notified by email.' : ''}`
        : 'Order updated successfully.'
    );
    setTimeout(() => setSuccessMsg(''), 4000);
    loadOrders();
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '—';
    return new Date(dateStr).toLocaleDateString('en-US', {
      month: 'short', day: 'numeric', year: 'numeric'
    });
  };

  const itemCount = (o) =>
    Array.isArray(o.line_items)
      ? o.line_items.reduce((sum, i) => sum + (Number(i.quantity) || 0), 0)
      : 0;

  const shippingSummary = (addr) => {
    if (!addr) return '—';
    const parts = [addr.city, addr.state, addr.country].filter(Boolean);
    return parts.length ? parts.join(', ') : '—';
  };

  const getStatusPill = (status) => {
    const cls = `admin-pill admin-pill-blank-${status || 'new'}`;
    return <span className={cls}>{STATUS_LABELS[status] || 'New'}</span>;
  };

  const handleExport = () => {
    if (orders.length === 0) return;
    const headers = 'Date,Order ID,Customer,Email,Items,Amount,Status,Carrier,Tracking,Shipping';
    const rows = orders.map(o => {
      const date = o.created_at ? new Date(o.created_at).toISOString().split('T')[0] : '';
      const shipping = shippingSummary(o.shipping_address);
      return `${date},"${o.order_id}","${o.customer_name || ''}","${o.client_email || ''}",${itemCount(o)},${o.amount || 0},${o.fulfillment_status || 'new'},${o.carrier || ''},${o.tracking_number || ''},"${shipping}"`;
    });
    const csv = [headers, ...rows].join('\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `tchpack-blanks-orders-${new Date().toISOString().split('T')[0]}.csv`;
    document.body.appendChild(a);
    a.click();
    a.remove();
    window.URL.revokeObjectURL(url);
  };

  const filteredOrders = (() => {
    const q = searchQuery.trim().toLowerCase();
    if (!q) return orders;
    return orders.filter((o) => {
      const haystack = [
        o.order_id,
        o.customer_name,
        o.client_email,
        o.tracking_number,
        o.carrier,
        o.project_title,
        o.quote_id
      ]
        .filter(Boolean)
        .join(' ')
        .toLowerCase();
      return haystack.includes(q);
    });
  })();

  const newCount = orders.filter(o => (o.fulfillment_status || 'new') === 'new').length;
  const processingCount = orders.filter(o => o.fulfillment_status === 'processing').length;
  const shippedCount = orders.filter(o => o.fulfillment_status === 'shipped').length;
  const totalAmount = orders.reduce((sum, o) => sum + Number(o.amount || 0), 0);

  return (
    <>
      {successMsg && (
        <div className="admin-success-banner">{successMsg}</div>
      )}

      <main className="admin-main">
        {/* Summary Bar */}
        <div className="admin-summary">
          <div className="admin-summary-card">
            <span className="admin-summary-label">New</span>
            <span className="admin-summary-value">{newCount}</span>
          </div>
          <div className="admin-summary-card">
            <span className="admin-summary-label">Processing</span>
            <span className="admin-summary-value">{processingCount}</span>
          </div>
          <div className="admin-summary-card">
            <span className="admin-summary-label">Shipped</span>
            <span className="admin-summary-value">{shippedCount}</span>
          </div>
          <div className="admin-summary-card">
            <span className="admin-summary-label">Total Amount</span>
            <span className="admin-summary-value">${totalAmount.toLocaleString('en-US', { minimumFractionDigits: 2 })}</span>
          </div>
        </div>

        {error && <div className="admin-error">{error}</div>}

        <div className="admin-table-wrapper">
          <div className="admin-table-header-bar">
            <h2>Blanks Orders</h2>
            <div className="admin-table-header-right">
              <div className="admin-search-box">
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/>
                </svg>
                <input
                  type="text"
                  className="admin-search-input"
                  placeholder="Search order ID, customer, tracking..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                />
                {searchQuery && (
                  <button className="admin-search-clear" onClick={() => setSearchQuery('')} aria-label="Clear search">
                    ×
                  </button>
                )}
              </div>
              <button className="admin-refresh-btn" onClick={loadOrders} disabled={isLoading}>
                Refresh
              </button>
              <button className="admin-export-btn" onClick={handleExport} disabled={orders.length === 0}>
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M21 15v4a2 2 0 01-2 2H5a2 2 0 01-2-2v-4"/><polyline points="7 10 12 15 17 10"/><line x1="12" y1="15" x2="12" y2="3"/>
                </svg>
                Export CSV
              </button>
              <span className="admin-table-count">{filteredOrders.length} items</span>
            </div>
          </div>

          {/* Status Tabs */}
          <div className="admin-tabs">
            {STATUS_TABS.map(tab => (
              <button
                key={tab.value}
                className={`admin-tab ${statusFilter === tab.value ? 'admin-tab-active' : ''}`}
                onClick={() => setStatusFilter(tab.value)}
              >
                {tab.label}
                {tab.value === 'new' && newCount > 0 && (
                  <span className="admin-tab-badge">{newCount}</span>
                )}
              </button>
            ))}
          </div>

          {isLoading ? (
            <div className="admin-loading">Loading orders...</div>
          ) : filteredOrders.length === 0 ? (
            <div className="admin-empty">
              <h3>{searchQuery ? 'No matching orders' : statusFilter ? `No ${statusFilter} orders` : 'No blanks orders yet'}</h3>
              <p>{searchQuery ? 'Try a different order ID, customer, or tracking number.' : statusFilter === 'new' ? 'All caught up! No new orders to fulfill.' : 'Orders will appear here as customers check out.'}</p>
            </div>
          ) : (
            <>
              <div className="admin-table-head">
                <span className="admin-col blank-col-order">Order</span>
                <span className="admin-col blank-col-customer">Customer</span>
                <span className="admin-col blank-col-items">Items</span>
                <span className="admin-col blank-col-amount">Amount</span>
                <span className="admin-col blank-col-shipping">Shipping</span>
                <span className="admin-col blank-col-tracking">Tracking</span>
                <span className="admin-col blank-col-status">Status</span>
                <span className="admin-col blank-col-date">Date</span>
                <span className="admin-col blank-col-action">Action</span>
              </div>

              {filteredOrders.map((o) => {
                const status = o.fulfillment_status || 'new';
                const firstItem = Array.isArray(o.line_items) && o.line_items[0];
                return (
                  <div key={o.order_id} className={`admin-table-row ${status === 'new' ? 'admin-row-pending' : ''}`}>
                    <span className="admin-col blank-col-order">
                      <span className="admin-quote-num">{o.order_id}</span>
                      <span className="admin-quote-type">{o.project_title || (o.payment_status ? o.payment_status : '—')}</span>
                    </span>
                    <span className="admin-col blank-col-customer">
                      <span className="admin-manu-name">{o.customer_name || '—'}</span>
                      <span className="admin-manu-company">{o.client_email || '—'}</span>
                    </span>
                    <span className="admin-col blank-col-items">
                      <span className="admin-quote-num">{itemCount(o)} item{itemCount(o) === 1 ? '' : 's'}</span>
                      {firstItem && (
                        <span className="admin-quote-type">
                          {firstItem.product}{Array.isArray(o.line_items) && o.line_items.length > 1 ? ` +${o.line_items.length - 1}` : ''}
                        </span>
                      )}
                    </span>
                    <span className="admin-col blank-col-amount admin-amount-value">
                      ${Number(o.amount || 0).toLocaleString('en-US', { minimumFractionDigits: 2 })}
                    </span>
                    <span className="admin-col blank-col-shipping">
                      {shippingSummary(o.shipping_address)}
                    </span>
                    <span className="admin-col blank-col-tracking">
                      {o.tracking_number ? (
                        <>
                          <span className="admin-quote-num">{o.tracking_number}</span>
                          <span className="admin-quote-type">{o.carrier || '—'}</span>
                        </>
                      ) : '—'}
                    </span>
                    <span className="admin-col blank-col-status">
                      {getStatusPill(status)}
                    </span>
                    <span className="admin-col blank-col-date">
                      {formatDate(o.created_at)}
                    </span>
                    <span className="admin-col blank-col-action">
                      <button className="admin-record-btn" onClick={() => setSelectedOrder(o)}>
                        {status === 'new' ? 'Fulfill' : 'Update'}
                      </button>
                    </span>
                  </div>
                );
              })}
            </>
          )}
        </div>
      </main>

      {selectedOrder && (
        <BlanksFulfillModal
          order={selectedOrder}
          onClose={() => setSelectedOrder(null)}
          onSubmit={handleFulfill}
        />
      )}
    </>
  );
};

export default BlanksOrders;
